
import {
  Label, SelectionResult, Clause, FileDocument, SelectionResultBox, Interval, IngestTextFragment, SavedQuery, Time,
  fileDocumentName, SelectionResultDoc, Realm, SelectionResultSliceComment, LabelAssignment, LabelIdentifier, DocumentFilter,
  SelectionResultGroup,
  ChatMessage,
} from '@/model';
import Vue from 'vue';
import DocumentEditor from '@/components/DocumentEditor.vue';
import IconButton from '@/components/IconButton.vue';
import FragmentsEditorDialog from '@/components/FragmentsEditorDialog.vue';
import LabeledTextWithContext from '@/components/LabeledTextWithContext.vue';
import SelectionResultLabelPanel from '@/components/SelectionResultLabelPanel.vue';
import DocumentPopover, { Goto } from '@/components/DocumentPopover.vue';
import SelectionQueryEditor from '@/components/SelectionQueryEditor.vue';
import ExpansionPanel from '@/components/ExpansionPanel.vue';
import DocumentHeader from '@/components/DocumentHeader.vue';
import { Splitpanes, Pane } from 'splitpanes';
import ChatPanel from '@/components/ChatPanel.vue';
import Computed from '@/components/Computed.vue';

import api from '@/rest-api';
import labels from '@/store/labels';
import {
  distinct, distinctBy, distinctByFn, notEmpty,
} from '@/util/array-util';
import RealmPicker from '@/components/RealmPicker.vue';
import realms from '@/store/realms';
import { merge } from '@/util/text-selection';
import FragmentLabelHistory from '@/components/FragmentLabelHistory.vue';
import SelectionTable from '@/components/tables/SelectionTable.vue';
import UserTable from '@/components/tables/UserTable.vue';
import Progress from '@/components/Progress.vue';
import IdentifiersTable from '@/components/tables/IdentifiersTable.vue';
import DocumentFilterEditor from '@/components/DocumentFilterEditor.vue';
import MarkdownTable from '@/components/MarkdownTable.vue';
import HtmlTable from '@/components/HtmlTable.vue';
import { summariesEnabled } from '@/store/ui-config';

export default Vue.extend({
  name: 'FragmentSelectionView',
  components: {
    DocumentEditor,
    LabeledTextWithContext,
    FragmentsEditorDialog,
    SelectionResultLabelPanel,
    DocumentPopover,
    SelectionQueryEditor,
    RealmPicker,
    IconButton,
    ExpansionPanel,
    DocumentHeader,
    FragmentLabelHistory,
    SelectionTable,
    UserTable,
    Progress,
    IdentifiersTable,
    DocumentFilterEditor,
    MarkdownTable,
    HtmlTable,
    Splitpanes,
    Pane,
    Computed,
    ChatPanel,
  },

  props: {
    query: { type: Object as () => SavedQuery },
    loading: { type: Boolean, default: false },
    documents: { type: Array as () => FileDocument[] },
    result: { type: Object as () => SelectionResult | undefined },
    realms: { type: Array as () => number[] | undefined },
    loadingBoxIds: { type: Array as () => number[], default: () => [] },
    queries: { type: Array as () => SavedQuery[], default: () => [] },
    assignments: { type: Map as any as () => Map<number, LabelAssignment[]> | undefined },
    identifiers: { type: Promise as any as () => Promise<LabelIdentifier[]> | undefined },
    chatHistory: { type: Array as () => ChatMessage[], default: () => [] },
    chatLoading: { type: Boolean, default: false },
    filter: { type: Object as () => DocumentFilter | undefined },
  },

  data() {
    return {
      goto: undefined as undefined | Goto,
      editDocument: undefined as undefined | FileDocument,
      editFragmentIds: undefined as undefined | number[],
      newFragment: undefined as undefined | IngestTextFragment,
      hovering: undefined as undefined | {
        boxId: number;
        labels: number[];
      },
      boxLimit: 100,
      panels: [] as number[],
      showHistory: false,
      tab: 0,
      chatWidth: 420,
      showOriginalBoxIds: [] as number[],
      originalTexts: {} as Record<number, string>,
    };
  },

  computed: {
    summariesEnabled(): boolean {
      return summariesEnabled();
    },
    labels(): Label[] {
      return labels.state.labels.filter((l) => l.selectable);
    },
    labelLookup(): ReadonlyMap<number, Label> {
      return labels.getters.lookup;
    },
    realm(): Realm[] {
      return this.realms ? this.realms.map((r) => realms.getters.lookup.get(r)).filter(notEmpty) : [];
    },
    isEditing(): boolean {
      return !!this.editFragmentIds;
    },
    documentsIds(): number[] {
      return distinct(this.result?.docs.map((d) => d.document.id) ?? []);
    },
    ingestionsIds(): number[] {
      return distinct(this.result?.docs.map((d) => d.ingestId) ?? []);
    },
    pagesIds(): number[] {
      return distinct(this.result?.docs.flatMap((d) => d.pages.map((p) => p.pageId)) ?? []);
    },
    boxesIds(): number[] {
      return distinct(this.result?.docs.flatMap((d) => d.pages.flatMap((p) => p.boxes.map((b) => b.boxId))) ?? []);
    },
    fragmentsIds(): number[] {
      return distinct(this.result?.docs
        .flatMap((d) => d.pages)
        .flatMap((p) => p.boxes)
        .flatMap((b) => b.slices)
        .flatMap((s) => s.fragments) ?? []);
    },
    totalPageCount(): number {
      const docs = distinctByFn(this.result?.docs ?? [], (d) => d.document.id);
      return docs.map((d) => d.totalPageCount).reduce((a, b) => a + b, 0) ?? 0;
    },
    filterActive(): boolean | undefined {
      return this.filter && Object.keys(this.filter).length > 0;
    },
  },

  watch: {
    result: {
      handler(result) {
        if (result && this.panels.length === 0) {
          const boxes = result.docs
            .flatMap((d) => d.pages)
            .flatMap((p) => p.boxes).length;
          this.panels = boxes < this.boxLimit || result.docs.length < 2 ? result.docs.map((_, i) => i) : [];
        }
      },
      immediate: true,
    },
    tab: {
      handler() {
        if (this.tab === 4) {
          this.$emit('loadIdentifiers');
        }
      },
      immediate: true,
    },
  },

  methods: {
    isHtmlTable(text: string): boolean {
      return !!text && text.trimStart().toLowerCase().startsWith('<table');
    },
    async toggleOriginal(box: SelectionResultBox) {
      if (this.showOriginalBoxIds.includes(box.boxId)) {
        this.showOriginalBoxIds = this.showOriginalBoxIds.filter((id) => id !== box.boxId);
        return;
      }
      this.showOriginalBoxIds = [...this.showOriginalBoxIds, box.boxId];
      if (this.originalTexts[box.boxId] === undefined) {
        this.originalTexts = {
          ...this.originalTexts,
          [box.boxId]: (await api.originalBoxText(box.boxId)).text,
        };
      }
    },
    isMarkdownTable(text: string): boolean {
      return !!text && text.trimStart().startsWith('|');
    },
    labelColor(label: Label) {
      return `rgba(${label.color.r}, ${label.color.g}, ${label.color.b}, 1)`;
    },
    emptyClause(): Clause {
      return {
        type: 'and',
        clauses: [],
      };
    },
    search() {
      this.panels = [];
      this.$emit('execute');
      if (this.tab === 4) {
        this.$emit('loadIdentifiers');
      }
    },
    intervals(box: SelectionResultBox): ReadonlyArray<Interval & { labels: readonly number[] }> {
      return box.slices.map((s) => ({ ...s, labels: s.labels.filter((l) => this.showingLabel(s.boxId, l)) }));
    },
    boxLabels(box: SelectionResultBox): Label[] {
      return distinct(
        box.slices.flatMap((i) => i.labels),
      )
        .map((l) => labels.getters.lookup.get(l))
        .filter(notEmpty);
    },
    editBoxFragments(box: SelectionResultBox) {
      this.editFragmentIds = box.slices.flatMap((s) => s.fragments);
    },
    closeEditDialog() {
      this.editFragmentIds = undefined;
      this.newFragment = undefined;
    },
    async createBoxFragment(box: SelectionResultBox) {
      this.newFragment = {
        id: 0,
        type: 'text',
        labels: [],
        boxes: merge([...box.slices]).map((m, i) => ({
          boxId: m.boxId, from: m.from, to: m.to, seqNo: i,
        })),
        isolate: false,
      };
      this.editBoxFragments(box);
    },
    saveFragment(fragment: IngestTextFragment) {
      this.$emit('saveFragment', fragment);
    },
    fragmentsChanged(fragments: IngestTextFragment[]) {
      this.$emit('fragmentsChanged', fragments);
    },
    fragmentsDeleted(fragmentIds: number[]) {
      this.$emit('fragmentsDeleted', fragmentIds);
    },
    documentSaved(document: FileDocument) {
      this.$emit('documentChanged', document);
    },
    showDocument(documentId: number, ingestId?: number, fragmentId?: number) {
      this.goto = {
        documentId,
        ingestId,
        fragmentId,
      };
    },
    showingLabel(boxId: number, labelId: number): boolean {
      return !!this.hovering && this.hovering.boxId === boxId && this.hovering.labels.includes(labelId);
    },
    onHoverLabels(boxId: number, ls: Label[], hover: boolean) {
      if (hover) {
        this.hovering = {
          boxId,
          labels: ls.map((l) => l.id),
        };
      } else {
        this.hovering = undefined;
      }
    },
    saveQuery(query: SavedQuery) {
      this.$emit('saveQuery', query);
    },
    dateTime(time: Time) {
      return time && Time.asString(time);
    },

    changeQuery(query: SavedQuery) {
      this.$emit('changeQuery', query);
    },

    changeFilter(documentFilter: Clause) {
      this.$emit('changeFilter', documentFilter);
    },

    changeRealms(rs: Realm[] | undefined) {
      this.$emit('changeRealms', rs?.map((r) => r.id));
    },

    input(query: SavedQuery) {
      this.$emit('input', query);
    },

    reset() {
      this.$emit('reset');
    },
    fileDocumentName(document: FileDocument) {
      return fileDocumentName(document);
    },
    numberOfBoxes(result: SelectionResultDoc) {
      return result.pages.flatMap((p) => p.boxes).length;
    },
    showingComments(box: SelectionResultBox): boolean {
      return box.slices.some((s) => s.comments.some(this.filterComment));
    },
    comments(box: SelectionResultBox): SelectionResultSliceComment[] {
      return distinctBy('fragmentId')(box.slices.flatMap((s) => s.comments).filter(this.filterComment));
    },
    filterComment(comment: SelectionResultSliceComment) {
      return comment.text.trim().length > 0;
    },
    history(ids: number[]) {
      return ids.flatMap((id) => this.assignments?.get(id)).filter(notEmpty);
    },
    allAssignments() {
      return this.assignments ? [...this.assignments.values()].flatMap((a) => a) : [];
    },
    slicesToGroups(slices: SelectionResultBox['slices']): SelectionResultGroup[][] {
      const groups = slices.flatMap((s) => s.groups);
      const nonIsolatedGroups = groups.filter((g) => !g.isolate);
      const keep: SelectionResultGroup[] = [];
      groups.filter((g) => g.isolate).forEach((g) => {
        if (!keep.some((k) => k.fragmentIds.every((x) => g.fragmentIds.includes(x)))) {
          keep.push(g);
        }
      });
      const isolatedGroups = keep.map((g) => [g]);
      return [nonIsolatedGroups, ...isolatedGroups].filter((g) => g.length > 0);
    },
    isIsolated(group: SelectionResultGroup[]): boolean {
      return group.some((s) => s.isolate);
    },
    startResize(e: MouseEvent) {
      const startX = e.clientX;
      const startWidth = this.chatWidth;
      const onMove = (ev: MouseEvent) => {
        this.chatWidth = Math.max(280, Math.min(900, startWidth + (startX - ev.clientX)));
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
  },
});
